import React, { useContext } from 'react'
import { ListMenu, ListMenuButton, Spacer, TabMenu, TabMenuButton, Text, View } from 'oio-react'
import { Route, Switch, useRouteMatch } from 'react-router-dom'
import { AddIcon } from 'assets/icons'
import EventDetailsBlock from 'src/sites/kits/Events/components/DetailsBlock'
import InitiativeMoreButton from 'src/sites/kits/Initiative/components/MoreButton'
import MessageFeed from 'src/sites/kits/Messages/components/Feed'
import NestedCustomPage from 'src/sites/kits/Object/apps/DynamicViewPage/NestedCustomPage'
import ObjectDescription from 'src/sites/kits/Object/elements/Description'
import ObjectTypeLabel from 'src/sites/kits/Object/components/TypeLabel'
import { Title } from 'src/sites/kits/UI'
import Popover from 'src/sites/kits/Utils/Popover'
import ScrollView from 'src/sites/kits/Utils/ScrollView'
import { useCreateInitiative, useOrganization } from 'src/core/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'
import { useGlobalState } from 'src/sites/state'

const SessionView = () => {
   const match = useRouteMatch()
   const { theme } = useGlobalState()

   const { organization } = useOrganization()
   const { createInitiative } = useCreateInitiative()
   const { initiative: session, initiativeLoading } = useContext(InitiativeHierarchyContext)

   if (initiativeLoading) {
      return null
   }

   const initiativeBody = session.body?.elements?.[0]?.body || ''
   const buttons = [{
      name: 'Overview',
      to: match.url,
      exact: true
   }].concat(session.customPages.map(page => ({
      name: page.title,
      to: `${match.url}/${page.slug}`
   })))

   const handleCreateMediaPage = async () => {
      await createInitiative({
         name: 'Files & Links',
         pageFormat: 'media',
         parentInitiativeId: session.id,
         privacy: 'inherit',
         typeId: organization.initiativeTypes.find(t => t.class === 'page').id
      })
   }

   return (
      <>
         <ScrollView
            position="relative"
            padding="20px[a] 30px[b] 50px[c-f]"
            width="100%"
            height="100%"
            scroll="on">
            <ObjectTypeLabel editUrl={`${match.url}/edit`} />
            <Spacer size="2" />
            <Title size="lg" color={theme.tmpContentTitleColor}>
               {session.name}
            </Title>
            {session.subtitle && (
               <View width="100%" marginTop="20px">
                  <Text
                     size="3[a-b] 4[c] 3[d] 4[e-f]"
                     lineHeight="130%"
                     color="#888">
                     {session.subtitle}
                  </Text>
               </View>
            )}
            <View
               marginTop="20px"
               display="flex"
               alignItems="center"
               width="100%"
               borderBottom={`1px solid ${theme.tmpContentLineColor}`}>
               <View
                  float="left"
                  width={session.currentUserCanEdit
                     ? 'calc(100% - 30px)'
                     : '100%'
                  }>
                  <TabMenu
                     buttonPaddingHorizontal="0px"
                     buttonSpacing="24px"
                     buttonSize="lg">
                     {buttons.map(button => (
                        <TabMenuButton
                           key={button.to}
                           name={button.name}
                           linkTo={button.to}
                           linkExact={button.exact}
                        />
                     ))}
                  </TabMenu>
               </View>
               {session.currentUserCanEdit && (
                  <Popover.Provider>
                     <Popover.Anchor>
                        <View float="left" width="30px">
                           <View
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              width="30px"
                              height="30px">
                              <AddIcon
                                 width="18px"
                                 height="18px"
                                 color="#888"
                                 strokeWidth="2px"
                              />
                           </View>
                        </View>
                     </Popover.Anchor>
                     <Popover.View
                        anchorOriginHorizontal="right"
                        anchorOriginVertical="top"
                        width="180px">
                        <View
                           float="left"
                           width="100%"
                           backgroundColor="#fff"
                           border="1px solid #eee"
                           borderRadius="3px"
                           boxShadow="6px 6px 30px rgba(0,0,0,0.1)">
                           <ListMenu buttonSize="sm">
                              <ListMenuButton
                                 onClick={handleCreateMediaPage}
                                 name="Add Files & Links Tab"
                              />
                           </ListMenu>
                        </View>
                     </Popover.View>
                  </Popover.Provider>
               )}
            </View>
            <View position="relative">
               <Switch>
                  <Route
                     exact
                     path={match.path}
                     render={() => (
                        <>
                           <EventDetailsBlock />
                           {initiativeBody && (
                              <View
                                 float="left"
                                 width="100%"
                                 paddingTop="25px">
                                 <ObjectDescription
                                    description={initiativeBody}
                                    textSize="lg"
                                 />
                              </View>
                           )}
                           {session.appsMessagesEnabled && (
                              <View float="left" width="100%" paddingTop="25px[a-b] 50px[c-f]">
                                 <View width="100%" paddingBottom="20px">
                                    <Text size="2" weight="medium">
                                       Comments
                                    </Text>
                                 </View>
                                 <MessageFeed
                                    currentUserCanCreate={
                                       session.appsMessagesCurrentUserCanCreate
                                    }
                                    currentUserCanModerate={
                                       session.currentUserCanModerateMessages
                                    }
                                    displayMode="feed"
                                    hasUnreadEntries={session.thread.hasUnreadEntries}
                                    messageLastViewed={session.thread.messageLastViewed}
                                 />
                              </View>
                           )}
                           <View
                              float="left"
                              width="100%"
                              height="90px[d-f]"
                           />
                        </>
                     )}
                  />
                  <Route
                     path={`${match.path}/:pageSlug([a-zA-Z0-9][^/]+)`}
                     render={() => (
                        <NestedCustomPage contentMaxWidth="600px" paddingHorizontal="0px" />
                     )}
                  />
               </Switch>
            </View>
         </ScrollView>
         <View
            position="absolute"
            top="0px"
            right="0px"
            margin="12px 50px">
            <InitiativeMoreButton
               color="transparent"
               editUrl={`${match.url}/edit`}
               iconColor="#888"
               iconSize="24px"
               popoverAnchorOriginVertical="top"
            />
         </View>
      </>
   )
}

export default SessionView
